import { forwardRef, useCallback, useMemo, type ForwardedRef } from "react";
import { createColumnHelper, Row } from "@tanstack/react-table";
import { DataTable } from "@clarityos/ui/data-table";
import type { InfiniteScrollProps } from "@/lib/query/tableProps";
import { formatDate } from "@/lib/time";
import { CardChip } from "@/features/payment/components/CardChip";
import { CardStatusIcon } from "@/features/payment/components/CardStatusIcon";
import { PatientMeta } from "@/features/patient";
import RequestCardViaSMSBlock from "@/features/patient/components/request-card-via-sms-block/RequestCardViaSMSBlock";
import { Button } from "@clarityos/ui/button";
import { CreditCard, Inbox } from "lucide-react";
import { usePatientStore } from "@/features/patient/store/usePatientStore";
import { AnalyticsEvent, trackEvent } from "@/lib/analytics";
import { CardCollectionRow } from "../types";
import { useBooleanFeatureFlag } from "@/lib/feature-flags/FeatureFlags";
import { FEATURE_FLAG_KEYS } from "@/lib/feature-flags/flags";
import { EmptyState, IconBadge } from "@clarityos/ui";

const columnHelper = createColumnHelper<CardCollectionRow>();

interface Props extends InfiniteScrollProps {
  items: CardCollectionRow[];
  isLoading: boolean;
}

function CardCollectionTableInner(
  {
    items,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  }: Props,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const setSelectedPatient = usePatientStore((s) => s.setSelectedPatient);
  const useResponsiveColumns = useBooleanFeatureFlag(
    FEATURE_FLAG_KEYS.cardCollectionResponsiveColumns,
  );

  const handleRowClick = useCallback(
    (row: Row<CardCollectionRow>) => {
      trackEvent(AnalyticsEvent.ManualCardCapture, {
        patient_id: row.original.patient.patientId,
      });
      setSelectedPatient({
        id: row.original.patient.patientId,
        initialData: row.original.patient,
      });
    },
    [setSelectedPatient],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("time", {
        header: "Time",
        size: 102,
        meta: useResponsiveColumns ? { naturalWidth: true } : undefined,
        cell: (cell) => (
          <span className="typo-body-sm text-typography-label">
            {formatDate(cell.getValue(), "h:mm a", cell.row.original.timeZone)}
          </span>
        ),
      }),
      columnHelper.display({
        id: "patient",
        header: "Patient",
        minSize: 460,
        meta: useResponsiveColumns ? { naturalWidth: true } : { grow: true },
        cell: (cell) => (
          <div className="flex flex-wrap items-center gap-3">
            <CardStatusIcon
              hasCard={!!cell.row.original.patient.cardOnFile?.last4}
            />
            <div className="flex flex-col gap-0.5">
              <span className="typo-body-bold-md text-action-hover">
                {cell.row.original.patient.firstName}{" "}
                {cell.row.original.patient.lastName}
              </span>
              <PatientMeta patient={cell.row.original.patient} />
            </div>
          </div>
        ),
      }),
      columnHelper.accessor("patient.cardOnFile", {
        header: "Card on File",
        size: 212,
        meta: useResponsiveColumns ? { naturalWidth: true } : undefined,
        cell: (cell) => (
          <CardChip
            type="details"
            cardInfo={cell.getValue() || null}
            className="gap-2"
          />
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        size: 294,
        meta: useResponsiveColumns ? { naturalWidth: true } : undefined,
        cell: (cell) =>
          !cell.row.original.patient.cardOnFile && (
            <div className="flex flex-wrap gap-1.5">
              <RequestCardViaSMSBlock
                surface="HomeScreen"
                patient={cell.row.original.patient}
              />
              <Button
                size="sm"
                variant="secondary"
                startIcon={<CreditCard strokeWidth={2.5} />}
                onClick={() => {
                  trackEvent(AnalyticsEvent.ManualCardCapture, {
                    patient_id: cell.row.original.patient.patientId,
                  });
                  setSelectedPatient({
                    id: cell.row.original.patient.patientId,
                    initialData: cell.row.original.patient,
                  });
                }}
              >
                Enter
              </Button>
            </div>
          ),
      }),
    ],
    [setSelectedPatient, useResponsiveColumns],
  );

  return (
    <DataTable
      ref={ref}
      data={items}
      columns={columns}
      loading={isLoading}
      refreshing={isFetching}
      loadingMore={isFetchingNextPage}
      rowClassName={(row) =>
        row.original.patient.cardOnFile
          ? ""
          : "shadow-[inset_4px_0px_0px_0px_var(--color-support-negative)]"
      }
      hasMore={hasNextPage}
      onLoadMore={fetchNextPage}
      onRowClick={handleRowClick}
      testId="patient-queue-table"
      getRowTestId={(row) =>
        `patient-queue-row-${row.original.patient.patientId}`
      }
      emptyState={
        <EmptyState
          icon={<IconBadge icon={Inbox} tone="disabled" />}
          title="No appointments for today"
          subtitle="Appointments scheduled for today will appear here."
        />
      }
    />
  );
}

export const CardCollectionTable = forwardRef(CardCollectionTableInner);
